import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, UpdateSubscription } from '../../../services/externalDataService.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { LoadingComponent } from '../../shared/loading/loading.component';

@Component({
  selector: 'app-update-subscription',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoadingComponent],
  templateUrl: './update-subscription.component.html',
  styleUrl: './update-subscription.component.css'
})
export class UpdateSubscriptionComponent implements OnInit {
  @Input() subscription!: Subscription;
  @Output() subscriptionUpdated = new EventEmitter<void>();
  @Output() modalClosed = new EventEmitter<void>();

  subscriptionForm: FormGroup;
  isLoading = false;
  errorMessage = '';

  // Shared data from service
  planTypes: any[] = [];
  cadenceOptions: any[] = [];
  statusOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private subscriptionService: SubscriptionService
  ) {
    this.subscriptionForm = this.fb.group({
      type: ['', [Validators.required]],
      cadence: ['', [Validators.required]],
      status: [1, [Validators.required]]
    });
    
    // Initialize shared data
    this.planTypes = this.subscriptionService.getPlanTypes();
    this.cadenceOptions = this.subscriptionService.getCadenceOptions();
    this.statusOptions = this.subscriptionService.getStatusOptions();
  }

  ngOnInit(): void {
    if (this.subscription) {
      this.subscriptionForm.patchValue({
        type: this.subscription.type,
        cadence: this.subscription.cadence,
        status: this.subscription.status
      });
    }
  }

  onSubmit(): void {
    if (this.subscriptionForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const formData = this.subscriptionForm.value;
      const updateData: UpdateSubscription = {
        subscriptionId: this.subscription.subscriptionId,
        type: Number(formData.type),
        cadence: Number(formData.cadence),
        status: Number(formData.status)
      };

      this.subscriptionService.updateSubscription(updateData).subscribe({
        next: () => {
          this.isLoading = false;
          alert('Subscription updated successfully');
          this.subscriptionUpdated.emit();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error?.error?.message || error?.message || 'Failed to update subscription';
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  onCancel(): void {
    this.modalClosed.emit();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.subscriptionForm.controls).forEach(key => {
      const control = this.subscriptionForm.get(key);
      control?.markAsTouched();
    });
  }

  // Helper methods for validation
  isFieldInvalid(fieldName: string): boolean {
    const field = this.subscriptionForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.subscriptionForm.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['min']) return `${fieldName} must be at least ${field.errors['min'].min}`;
    }
    return '';
  }

  // Get type text - delegate to service
  getTypeText(type: number): string {
    return this.subscriptionService.getTypeText(type);
  }

  // Get cadence text - delegate to service
  getCadenceText(cadence: number): string {
    return this.subscriptionService.getCadenceText(cadence);
  }

  // Get payment method text from current subscription
  getCurrentPaymentMethod(): string {
    return this.subscriptionService.getPaymentMethodText(this.subscription.paymentMethod);
  }

  // Get payment status text from current subscription
  getCurrentPaymentStatus(): string {
    return this.subscriptionService.getPaymentStatusText(this.subscription.paymentStatus);
  }

  // Format currency
  formatCurrency(amount: number, currency: string = 'INR'): string {
    return this.subscriptionService.formatCurrency(amount, currency);
  }
}
